import React, { useEffect, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { deleteUserEducation, fetchUserEducationList } from "../../../services/api/education";
import dayjs from "dayjs";
import { EducationType } from "../../../utils/enum";
import AddEducationModal from "../../CompleteYourProfile/components/AddEducationModal";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const AddEditEducation = ({ userId }) => {
  const dispatch = useDispatch();
  const [isAddEducationModalOpen, setIsAddEducationModalOpen] = useState(false);
  const [educationToDelete, setEducationToDelete] = useState(null);
  const [selectedUserEducation, setSelectedUserEducation] = useState();
  const [userEducation, setUserEducation] = useState([]);
  const isEdit = selectedUserEducation ? true : false;
  const {
    data: userEducationListData,
    isPending: isFetchEducationListLoading,
    error: isFetchEducationListError,
    refetch: refetchUserEducationList,
  } = useQuery({
    queryKey: ["userEducationList"],
    queryFn: () => fetchUserEducationList(userId),
    enabled: !!userId,
  });

  const {
    isPending: isDeleteEducationLoading,
    error: isDeleteEducationError,
    data: educationDeleteData,
    mutateAsync: deleteEducationMutation,
  } = useMutation({
    mutationFn: (id) => deleteUserEducation(id, userId),
    enabled: false,
  });

  useEffect(() => {
    if (userEducationListData) {
      setUserEducation(userEducationListData?.data);
    }
  }, [userEducationListData]);

  const editEducationButtonClicked = (education) => {
    try {
      setSelectedUserEducation(education);
      setIsAddEducationModalOpen(true);
    } catch (error) {}
  };

  const handleEducationDelete = async () => {
    if (!educationToDelete) return;
    try {
      await deleteEducationMutation(educationToDelete.id);

      const filteredUserEducation = userEducation.filter(
        (education) => education.id !== educationToDelete.id,
      );
      setUserEducation(filteredUserEducation);
      setEducationToDelete(null);
      dispatch(
        showToast({
          message: "Education deleted successfully",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to delete education",
          type: "error",
        }),
      );
    }
  };

  return (
    <div className="mt-6 border-b pb-4">
      <div className="bg-light px-4 py-2 rounded-md flex justify-between items-center ">
        <h6 className="text-dark font-normal">Education</h6>
        <i
          className="fa-solid fa-circle-plus cursor-pointer text-lg text-secondary"
          onClick={() => setIsAddEducationModalOpen(true)}
        ></i>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 my-4 px-4">
        {userEducation?.map((education) => (
          <div className="bg-light rounded-md py-3 px-4 flex justify-between border">
            <div>
              <div className="flex mb-1.5">
                <p className="font-medium w-36">Institution Name: </p>
                <p>{education?.institutionName}</p>
              </div>
              <div className="flex mb-1.5">
                <p className="font-medium w-36">Education Type: </p>
                <p>{EducationType[education?.educationType]}</p>
              </div>
              <div className="flex">
                <div className="flex ">
                  <p className="font-medium">Start Date: </p>
                  <p className="ml-2">{dayjs(education?.startDate).format("DD/MM/YYYY")}</p>
                </div>
                <div className="flex  ml-9">
                  <p className="font-medium">End Date: </p>
                  <p className="ml-2">{dayjs(education?.endDate).format("DD/MM/YYYY")}</p>
                </div>
              </div>
            </div>
            <div>
              <i
                className="fa-regular fa-trash-can text-red-600 cursor-pointer mr-3"
                onClick={() => setEducationToDelete(education)}
              ></i>
              <i
                className="fa-light fa-pencil text-gray-900 cursor-pointer"
                onClick={() => editEducationButtonClicked(education)}
              ></i>
            </div>
          </div>
        ))}
      </div>

      {educationToDelete && (
        <BaseConfirmModal
          open={!!educationToDelete}
          onClose={() => setEducationToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Education"
          description={`Are you sure you want to delete "${educationToDelete?.institutionName}"? This action cannot be undone.`}
          isLoading={isDeleteEducationLoading}
          onConfirm={handleEducationDelete}
        />
      )}

      {isAddEducationModalOpen && (
        <AddEducationModal
          open={isAddEducationModalOpen}
          close={setIsAddEducationModalOpen}
          refetchUserEducationList={refetchUserEducationList}
          isEdit={isEdit}
          setSelectedUserEducation={setSelectedUserEducation}
          selectedUserEducation={selectedUserEducation}
          targetUserId={userId}
        />
      )}
    </div>
  );
};

export default AddEditEducation;
